import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — "chemicals" is the horizontal member of the
 * chemical-formulation family in the ESPR working plan 2025-2030; it models a
 * REACH-registered substance or mixture placed on the EU market, carrying
 * CLP hazard classification, REACH registration/authorisation/restriction
 * status, SCIP notification and the Safety Data Sheet. Widest of the family —
 * detergent/paint/lubricant are specialised subsets of this shape.
 *
 * Field ids are STABLE; trued up when the delegated act lands by adding fields
 * / flipping `required`/`tier`, never by renaming ids.
 */
export const chemicalV1: PassportSchemaDef = {
  id: "chemical-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Kimyasal Ürün Pasaportu",
    en: "Chemical Product Passport",
    de: "Chemikalien-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/chemical/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.chemicalType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["substance", "mixture"],
          labels: { tr: "Kimyasal türü", en: "Chemical type", de: "Chemikalientyp" },
        },
        {
          id: "identity.tradeName",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ticari ad", en: "Trade name", de: "Handelsname" },
        },
        {
          id: "identity.casNumber",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "CAS numarası", en: "CAS number", de: "CAS-Nummer" },
          description: "For a single substance.",
          highlight: true,
        },
        {
          id: "identity.ecNumber",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "EC numarası", en: "EC number", de: "EG-Nummer" },
        },
        {
          id: "identity.reachRegistrationNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "REACH kayıt numarası",
            en: "REACH registration number",
            de: "REACH-Registrierungsnummer",
          },
        },
        {
          id: "identity.physicalState",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["solid", "liquid", "gas", "powder", "gel", "other"],
          labels: { tr: "Fiziksel hal", en: "Physical state", de: "Aggregatzustand" },
          highlight: true,
        },
        {
          id: "identity.brand",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Üretici/İthalatçı adı",
            en: "Manufacturer/Importer name",
            de: "Hersteller-/Importeursname",
          },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.netContent",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Net içerik", en: "Net content", de: "Nettoinhalt" },
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
      ],
    },
    {
      id: "hazards",
      labels: {
        tr: "Tehlike Sınıflandırması (CLP)",
        en: "Hazard Classification (CLP)",
        de: "Gefahreneinstufung (CLP)",
      },
      fields: [
        {
          id: "hazards.clpSignalWord",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["danger", "warning", "none"],
          labels: { tr: "CLP uyarı kelimesi", en: "CLP signal word", de: "CLP-Signalwort" },
          highlight: true,
        },
        {
          id: "hazards.hazardStatements",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Tehlike ifadeleri (H)", en: "Hazard statements (H)", de: "Gefahrenhinweise (H)" },
          description: "H-codes assigned under CLP, e.g. H302, H314.",
        },
        {
          id: "hazards.precautionaryStatements",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Önlem ifadeleri (P)",
            en: "Precautionary statements (P)",
            de: "Sicherheitshinweise (P)",
          },
        },
        {
          id: "hazards.ghsPictograms",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "GHS piktogramları", en: "GHS pictograms", de: "GHS-Piktogramme" },
        },
        {
          id: "hazards.hazardClasses",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Tehlike sınıfları", en: "Hazard classes", de: "Gefahrenklassen" },
          description: "E.g. Acute Tox. 4, Skin Corr. 1B.",
        },
        {
          id: "hazards.pbtOrVpvb",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "PBT/vPvB özellikleri",
            en: "PBT/vPvB properties",
            de: "PBT/vPvB-Eigenschaften",
          },
          description: "Persistent, bioaccumulative and toxic / very persistent very bioaccumulative.",
        },
      ],
    },
    {
      id: "composition",
      labels: { tr: "Bileşim", en: "Composition", de: "Zusammensetzung" },
      fields: [
        {
          id: "composition.compositionSummary",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bileşim özeti", en: "Composition summary", de: "Zusammensetzungsübersicht" },
        },
        {
          id: "composition.substanceList",
          type: "group",
          required: false,
          tier: "public",
          labels: { tr: "Madde listesi", en: "Substance list", de: "Stoffliste" },
          description: "Constituents of a mixture (name / CAS / EC / concentration / classification).",
          itemFields: [
            {
              key: "name",
              type: "string",
              required: true,
              labels: { tr: "Ad", en: "Name", de: "Name" },
            },
            {
              key: "casNumber",
              type: "string",
              required: false,
              labels: { tr: "CAS numarası", en: "CAS number", de: "CAS-Nummer" },
            },
            {
              key: "ecNumber",
              type: "string",
              required: false,
              labels: { tr: "EC numarası", en: "EC number", de: "EG-Nummer" },
            },
            {
              key: "concentrationRange",
              type: "string",
              required: false,
              labels: { tr: "Konsantrasyon aralığı", en: "Concentration range", de: "Konzentrationsbereich" },
            },
            {
              key: "classification",
              type: "string",
              required: false,
              labels: { tr: "Sınıflandırma", en: "Classification", de: "Einstufung" },
            },
          ],
        },
        {
          id: "composition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          highlight: true,
        },
        {
          id: "composition.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
        },
        {
          id: "composition.impurities",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Safsızlıklar", en: "Impurities", de: "Verunreinigungen" },
        },
      ],
    },
    {
      id: "regulatory",
      labels: { tr: "Mevzuat Durumu", en: "Regulatory Status", de: "Regulatorischer Status" },
      fields: [
        {
          id: "regulatory.reachStatus",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["registered", "exempt", "not-required", "pre-registered"],
          labels: { tr: "REACH durumu", en: "REACH status", de: "REACH-Status" },
          highlight: true,
        },
        {
          id: "regulatory.authorisationRequired",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "İzin gerektirir (Ek XIV)",
            en: "Authorisation required (Annex XIV)",
            de: "Zulassungspflichtig (Anhang XIV)",
          },
        },
        {
          id: "regulatory.restrictionsApplicable",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Kısıtlamalar geçerli (Ek XVII)",
            en: "Restrictions applicable (Annex XVII)",
            de: "Beschränkungen anwendbar (Anhang XVII)",
          },
        },
        {
          id: "regulatory.scipNumber",
          deprecatedBy: "compliance.scipNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "SCIP kayıt numarası", en: "SCIP number", de: "SCIP-Nummer" },
          description: "ECHA SCIP database reference for articles containing SVHC.",
        },
        {
          id: "regulatory.picExportNotification",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "PIC ihracat bildirimi",
            en: "PIC export notification",
            de: "PIC-Ausfuhrnotifizierung",
          },
          description: "Prior Informed Consent (Regulation (EU) 649/2012) applies.",
        },
      ],
    },
    {
      id: "safety",
      labels: { tr: "Güvenlik ve Bertaraf", en: "Safety & Disposal", de: "Sicherheit & Entsorgung" },
      fields: [
        {
          id: "safety.storageConditions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Depolama koşulları", en: "Storage conditions", de: "Lagerbedingungen" },
        },
        {
          id: "safety.incompatibilities",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Uyumsuzluklar", en: "Incompatibilities", de: "Unverträglichkeiten" },
        },
        {
          id: "safety.disposalInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bertaraf talimatları", en: "Disposal instructions", de: "Entsorgungshinweise" },
        },
        {
          id: "safety.sdsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Güvenlik Bilgi Formu (SDS)",
            en: "Safety Data Sheet (SDS)",
            de: "Sicherheitsdatenblatt (SDB)",
          },
        },
        {
          id: "safety.transportClass",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Taşıma sınıfı (ADR/UN)",
            en: "Transport class (ADR/UN)",
            de: "Transportklasse (ADR/UN)",
          },
        },
        {
          id: "safety.emergencyContact",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Acil durum iletişimi", en: "Emergency contact", de: "Notfallkontakt" },
        },
      ],
    },
  ],
};
